/**
 * ApprovedProjectsEditor - Editor for approved projects/laws
 * Allows adding, editing, reordering and removing projects
 */

import { useState } from 'react';
import {
  ApprovedProject,
  BRAZILIAN_POLITICAL_POSITIONS,
  BrazilianPoliticalPosition,
} from '@/types/tenant.types';

interface ApprovedProjectsEditorProps {
  projects: ApprovedProject[];
  onChange: (projects: ApprovedProject[]) => void;
  disabled?: boolean;
  maxProjects?: number;
}

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 50 }, (_, i) => currentYear - i);

export function ApprovedProjectsEditor({
  projects,
  onChange,
  disabled = false,
  maxProjects = 50,
}: ApprovedProjectsEditorProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<ApprovedProject>({
    name: '',
    mandate: 'Vereador',
    year: currentYear,
    description: '',
  });

  const resetForm = () => {
    setFormData({
      name: '',
      mandate: 'Vereador',
      year: currentYear,
      description: '',
    });
    setIsAdding(false);
    setEditingIndex(null);
  };

  const handleAdd = () => {
    if (!formData.name.trim() || disabled) return;
    onChange([
      ...projects,
      { ...formData, name: formData.name.trim(), description: formData.description?.trim() },
    ]);
    resetForm();
  };

  const handleEdit = () => {
    if (editingIndex === null || !formData.name.trim() || disabled) return;
    const updated = [...projects];
    updated[editingIndex] = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description?.trim(),
    };
    onChange(updated);
    resetForm();
  };

  const handleRemove = (index: number) => {
    if (disabled) return;
    onChange(projects.filter((_, i) => i !== index));
  };

  const startEditing = (index: number) => {
    if (disabled) return;
    setEditingIndex(index);
    setFormData(projects[index]);
    setIsAdding(false);
  };

  const moveProject = (index: number, direction: 'up' | 'down') => {
    if (disabled) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= projects.length) return;

    const updated = [...projects];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  const canAdd = projects.length < maxProjects;

  return (
    <div className="space-y-4">
      {/* Projects List */}
      {projects.length > 0 && (
        <div className="space-y-2">
          {projects.map((project, index) => (
            <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg group">
              {/* Reorder buttons */}
              <div className="flex flex-col gap-0.5 pt-1">
                <button
                  onClick={() => moveProject(index, 'up')}
                  disabled={index === 0 || disabled}
                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Mover para cima"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => moveProject(index, 'down')}
                  disabled={index === projects.length - 1 || disabled}
                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Mover para baixo"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>

              {/* Project Icon */}
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>

              {/* Project Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{project.name}</p>
                <p className="text-sm text-gray-600">
                  {project.mandate} - {project.year}
                </p>
                {project.description && (
                  <p className="text-sm text-gray-500 mt-1">{project.description}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => startEditing(index)}
                  disabled={disabled}
                  className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                  title="Editar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleRemove(index)}
                  disabled={disabled}
                  className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  title="Remover"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form */}
      {(isAdding || editingIndex !== null) && (
        <div className="p-4 border-2 border-dashed border-blue-300 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">
            {editingIndex !== null ? 'Editar Projeto' : 'Novo Projeto'}
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Projeto/Lei *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Lei da Transparência Municipal"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mandato *</label>
                <select
                  value={formData.mandate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      mandate: e.target.value as BrazilianPoliticalPosition,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {BRAZILIAN_POLITICAL_POSITIONS.map((position) => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ano *</label>
                <select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição (opcional)
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descrição do projeto aprovado..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={editingIndex !== null ? handleEdit : handleAdd}
                disabled={!formData.name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingIndex !== null ? 'Salvar' : 'Adicionar'}
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Button */}
      {!isAdding && editingIndex === null && canAdd && (
        <button
          onClick={() => setIsAdding(true)}
          disabled={disabled}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Adicionar Projeto Aprovado
        </button>
      )}

      {/* Max reached message */}
      {!canAdd && (
        <p className="text-sm text-amber-600 text-center">
          Limite máximo de {maxProjects} projetos atingido
        </p>
      )}
    </div>
  );
}
